import axios from "axios";
import { ArrowLeft, Shield } from "iconsax-react";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { SurveyData } from "../types/survey";
import { getContrastingColor } from "../utils/color";
import { pathAPI } from "../utils/pathAPI";
import { getQueryString } from "../utils/query";
import { isValidEmail, isValidPhoneNumber } from "../utils/validate";
import { MediaSkeleton } from "./common/MediaSkeleton";

interface PropTypes {
  showSurvey: boolean;
  otpData: { otp: string };
  surveyData: any;
  setSurveyData: any;
  responsiveImageUrl: string;
  isImageLoading: boolean;
  contentCountdown: number;
  ReactGA: any;
  adSettings?: any;
  onDataTracking: (clickNumber: number, activityType: string, activityDetails: string) => void;
  onClickWithTag: (clickTag: string | undefined, isDependence?: boolean) => void;
  logoUrl: string;
  onSubmit: () => void;
  onSubmitSurvey: (surveyData: SurveyData) => void;
  isLogoLoading: boolean;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}
const Survey2 = ({ showSurvey, otpData, surveyData, setSurveyData, responsiveImageUrl, isImageLoading, contentCountdown, ReactGA, logoUrl, adSettings, onDataTracking, onClickWithTag, onSubmit, onSubmitSurvey, onInputChange }: PropTypes) => {
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResendDisabled, setIsResendDisabled] = useState(false);
  const intl = useIntl();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showOtp, setShowOtp] = useState(false);
  const [showSurveyOtp, setShowSurveyOtp] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendCooldown > 0) {
      timer = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(timer); // Xóa interval ngay khi về 0
            setIsResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
    // Chỉ chạy lại khi resendCooldown chuyển từ 0 sang một giá trị dương (lúc bấm gửi OTP)
  }, [resendCooldown > 0]);

  useEffect(() => {
    if (showOtp && resendCooldown === 0 && !isResendDisabled) {
      setIsResendDisabled(true);
      setResendCooldown(60); // Start with 60 seconds cooldown
    }
  }, [showOtp]);

  const [isAgree, setIsAgree] = useState(true);

  const haveOptionalSurvey =
    surveyData.optionalSurveyAnswer !== ""
      ? {
          optionalSurveyQuestion: adSettings?.optional_question,
          optionalSurveyAnswer: surveyData.optionalSurveyAnswer,
        }
      : {
          optionalSurveyQuestion: null,
          optionalSurveyAnswer: null,
          optionalSurveyAnsExtend: null,
        };

  const isFormValid = () => {
    const requiredFields: string[] = [];

    if (adSettings?.fullname === "true" && !surveyData.fullName?.trim()) {
      requiredFields.push(intl.formatMessage({ id: "adRenderer.fullName" }) + " is required");
    } else if (adSettings?.fullname === "true" && surveyData.fullName?.trim().length < 2) {
      requiredFields.push(
        `${intl.formatMessage({
          id: "adRenderer.fullName",
        })} ${intl.formatMessage({
          id: "adRenderer.validation.minLength",
        })}`,
      );
    }

    if (adSettings?.phone_number === "true" && !surveyData.phoneNumber) {
      requiredFields.push(intl.formatMessage({ id: "adRenderer.phoneNumber" }) + " is required");
    } else if (adSettings?.phone_number === "true" && !isValidPhoneNumber(surveyData.phoneNumber)) {
      requiredFields.push(
        `${intl.formatMessage({
          id: "adRenderer.phoneNumber",
        })} ${intl.formatMessage({
          id: "adRenderer.validation.invalidPhone",
        })}`,
      );
    }

    if (adSettings?.email === "true" && !surveyData.email) {
      requiredFields.push(intl.formatMessage({ id: "adRenderer.email" }) + " is required");
    } else if (adSettings?.email === "true" && !isValidEmail(surveyData.email)) {
      requiredFields.push(
        `${intl.formatMessage({ id: "adRenderer.email" })} ${intl.formatMessage({
          id: "adRenderer.validation.invalidEmail",
        })}`,
      );
    }

    if (adSettings?.BoD === "true" && !surveyData.birthdate) {
      requiredFields.push(intl.formatMessage({ id: "adRenderer.birthdate" }) + " is required");
    }

    if (adSettings?.gender === "true" && !surveyData.gender) {
      requiredFields.push(intl.formatMessage({ id: "adRenderer.gender" }) + " is required");
    }

    return requiredFields;
  };

  const handleResendOTP = async () => {
    if (!isResendDisabled) {
      const responseReSendOTP = await axios.post(`${process.env.REACT_APP_BACKEND_URL + pathAPI.generateOTP}`, { phoneNumber: surveyData.phoneNumber });

      if (responseReSendOTP.data.code === 0) {
        setIsResendDisabled(true);
        setResendCooldown(60); // Set 60 seconds cooldown
        onDataTracking(1, "click", "User clicked resend OTP");
        ReactGA.event({
          category: "Survey",
          action: "Resend OTP",
          label: "OTP Resend",
        });
      } else {
        alert(intl.formatMessage({ id: "adRenderer.otpSendError" }));
      }
    }

    // Bật khi demo
    // setIsResendDisabled(true);
    // setResendCooldown(60); // Set 60 seconds cooldown
  };

  const handleSubmitSurvey = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSubmitting(true);
    const errors = isFormValid();
    try {
      if (adSettings?.is_otp_enable === "true") {
        const responseVerifyPhone = await axios.post(`${process.env.REACT_APP_BACKEND_URL + pathAPI.verifyPhoneNumber}`, {
          phoneNumber: surveyData.phoneNumber,
          ssid: getQueryString("ssid"),
          adId: adSettings.id,
        });

        if (responseVerifyPhone.data.code === 0) {
          if (errors.length === 0 && adSettings) {
            const responseReSendOTP = await axios.post(`${process.env.REACT_APP_BACKEND_URL + pathAPI.generateOTP}`, { phoneNumber: surveyData.phoneNumber });
            console.log("handleSubmitSurvey: generateOTP response:", responseReSendOTP.data);
            if (responseReSendOTP.data.code === 0) {
              setValidationErrors([]);
              setShowOtp(true);
            } else {
              setValidationErrors([intl.formatMessage({ id: "adRenderer.otpSendError" })]);
            }
          } else {
            setValidationErrors(errors);
          }
        } else if (responseVerifyPhone.data.code === -2) {
          setValidationErrors([]);
          onDataTracking(1, "click", "User click access");
          await onClickWithTag(adSettings?.impression_tag_3rd_party_click, false);
          await onSubmit();
          onSubmitSurvey({ ...surveyData, ...haveOptionalSurvey });
        } else {
          setValidationErrors([intl.formatMessage({ id: "adRenderer.otpSendError" })]);
        }
      } else {
        if (errors.length === 0 && adSettings) {
          setValidationErrors([]);
          onDataTracking(1, "click", "User click access");
          await onClickWithTag(adSettings?.impression_tag_3rd_party_click, false);
          await onSubmit();
          onSubmitSurvey({ ...surveyData, ...haveOptionalSurvey });
        } else {
          setValidationErrors(errors);
        }
      }
    } catch (error) {
      setValidationErrors([intl.formatMessage({ id: "adRenderer.otpSendError" })]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitFormSurvey = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowSurveyOtp(true);
    const pageHeader = document.getElementById("header");
    const logoPartner = document.getElementById("logoPartner") as HTMLImageElement;

    if (pageHeader && !showSurvey) {
      pageHeader.style.display = "block";
      logoPartner.src = logoUrl;
    }
  };

  const handleSubmitSurveyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = isFormValid();
    setIsSubmitting(true);

    try {
      if (errors.length === 0 && adSettings) {
        const submitOtp = otpData.otp.trim();
        if (!submitOtp) {
          setValidationErrors([intl.formatMessage({ id: "adRenderer.otpRequired" })]);
          return;
        }
        if (submitOtp.length < 6) {
          setValidationErrors([intl.formatMessage({ id: "adRenderer.otpMinLength" })]);
          return;
        }
        const responseSubmitOtp = await axios.post(`${process.env.REACT_APP_BACKEND_URL + pathAPI.submitOTP}`, { otp: otpData.otp });

        if (responseSubmitOtp.data.code !== 0) {
          setValidationErrors([intl.formatMessage({ id: "adRenderer.otpInvalid" })]);
          return;
        } else {
          surveyData.isSendOTP = "true";
          setValidationErrors([]);
          onDataTracking(1, "click", "User click otp");
          ReactGA.event({
            category: "Survey",
            action: "Submit Survey",
            label: "Survey Form",
          });
          await onSubmit();
          onSubmitSurvey({ ...surveyData, ...haveOptionalSurvey });
          await onClickWithTag(adSettings?.impression_tag_3rd_party_click, false);

          // setShowSurveyOtp(false);
        }
      }
    } catch (error) {
      console.error("handleSubmitSurveyOtp: Error:", error);
      setValidationErrors([intl.formatMessage({ id: "adRenderer.otpInvalid" })]);
    } finally {
      setIsSubmitting(false);
    }

    // Bật khi demo
    // onSubmit();
    // surveyData.isSendOTP = "true";
    // onSubmitSurvey({ ...surveyData, ...haveOptionalSurvey });
  };

  return (
    <>
      {!showSurveyOtp ? (
        <div className={`w-full h-full flex flex-col xl:flex-row overflow-hidden slide-in-right`} style={{ backgroundColor: adSettings?.background_color }}>
          {/* Media Section */}
          <div className="flex-1 min-h-0 flex flex-col justify-center items-center relative">
            {isImageLoading ? (
              <div className="w-full h-full flex items-center justify-center">
                <div className="w-full h-full lg:h-auto aspect-video shadow-sm overflow-hidden max-h-full bg-gray-200">
                  <MediaSkeleton className="bg-transparent" />
                </div>
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center overflow-hidden">
                <img src={responsiveImageUrl || require("../assets/company_logo.jpg")} alt="Banner" className="w-full h-full object-contain max-h-full" />
              </div>
            )}
          </div>

          {/* Button Section */}
          <div className="w-full xl:w-[450px] flex-shrink-0 p-4 xl:p-8 flex flex-col items-center justify-center xl:border-l xl:border-gray-100/10 bg-white/5">
            <button
              onClick={(e) => {
                handleSubmitFormSurvey(e);
              }}
              disabled={contentCountdown > 0}
              className="w-full max-w-sm px-5 py-3 md:px-8 md:py-4 rounded-full font-bold uppercase text-base md:text-lg tracking-wide disabled:opacity-75 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 transition-all duration-300 animated-button"
              style={{
                backgroundColor: adSettings?.button_color || "#e60000",
                color: getContrastingColor(adSettings?.button_color || "#e60000"),
              }}
            >
              {intl.locale === "en"
                ? adSettings?.button_text_en
                : adSettings?.button_text ||
                  intl.formatMessage({
                    id: "adRenderer.connectInternet",
                  })}
              {contentCountdown > 0 && ` (${contentCountdown}s)`}
            </button>
          </div>
        </div>
      ) : (
        <div className="block w-full h-full overflow-y-auto">
          <div className="flex items-center justify-center w-full min-h-full">
            <div className="flex w-full max-w-md mx-auto rounded-lg">
              {showOtp ? (
                <div className="min-h-[calc(100vh-203px)] md:min-h-[calc(100vh-143px)] bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
                  <div className="w-full max-w-md">
                    <form
                      id="surveyForm"
                      onSubmit={handleSubmitSurveyOtp}
                      className="
                                            bg-white 
                                           overflow-hidden"
                    >
                      {/* Header */}
                      <div
                        style={{
                          background: adSettings?.button_color || "#3b82f6",
                        }}
                        className="p-4 text-center"
                      >
                        <div className="flex justify-center mb-3">
                          <div className="bg-white/20 rounded-full p-3">
                            <Shield className="w-8 h-8 text-white" />
                          </div>
                        </div>
                        <h1 className="text-xl font-bold text-white mb-2">
                          {intl.formatMessage({
                            id: "adRenderer.surveyOtptitle",
                          })}
                        </h1>
                        <p className="text-blue-100 text-sm">
                          {intl.formatMessage({
                            id: "adRenderer.surveyOtpsubtitle",
                          })}
                        </p>
                      </div>

                      {/* Form Content */}
                      <div className="px-8 py-6">
                        {/* OTP Input */}
                        <div className="mb-6">
                          <label htmlFor="otp" className="block text-sm font-semibold text-gray-700 mb-3">
                            {intl.formatMessage({ id: "adRenderer.otp" })}
                            <span className="text-red-500 ml-1">*</span>
                          </label>
                          <input
                            type="text"
                            id="otp"
                            name="otp"
                            maxLength={6}
                            value={otpData.otp}
                            onChange={onInputChange}
                            placeholder={intl.formatMessage({
                              id: "adRenderer.otpPlaceholder",
                            })}
                            className="w-full px-4 py-4 text-center text-2xl font-mono tracking-widest border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all duration-200 bg-gray-50 hover:bg-white"
                            required
                          />
                        </div>

                        {/* Resend Button */}
                        <div className="mb-6">
                          <button
                            type="button"
                            onClick={handleResendOTP}
                            disabled={isResendDisabled || isSubmitting}
                            className="w-full py-3 text-blue-600 font-medium hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 bg-blue-50 hover:bg-blue-100 rounded-lg disabled:hover:bg-blue-50"
                          >
                            {resendCooldown > 0
                              ? `${intl.formatMessage({
                                  id: "resendOtp",
                                })} (${resendCooldown}s)`
                              : intl.formatMessage({ id: "resendOtp" })}
                          </button>
                        </div>

                        {/* Error Messages */}
                        {validationErrors.length > 0 && (
                          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
                            <div className="flex items-start space-x-3">
                              <div>
                                <ul className="space-y-1">
                                  {validationErrors.map((error, index) => (
                                    <li key={index} className="text-red-700 text-sm">
                                      • {error}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => {
                              setShowOtp(false);
                              setValidationErrors([]);
                            }}
                            className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-colors duration-200"
                          >
                            <ArrowLeft className="w-4 h-4" />
                            {intl.formatMessage({ id: "return" })}
                          </button>

                          <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex-1 py-3 px-4 font-semibold text-white rounded-xl transition-all duration-200 hover:shadow-lg disabled:opacity-75 disabled:cursor-not-allowed"
                            style={{
                              backgroundColor: adSettings?.button_color || "#3b82f6",
                            }}
                          >
                            {isSubmitting ? (
                              <div className="flex items-center justify-center gap-2">
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                Đang xử lý...
                              </div>
                            ) : (
                              intl.formatMessage({ id: "adRenderer.confirm" })
                            )}
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              ) : (
                <form id="surveyForm" onSubmit={handleSubmitSurvey} className="relative bg-gradient-to-br from-white via-gray-50 to-blue-50 p-3 py-10 shadow-2xl w-full survey-form border border-gray-100 overflow-hidden">
                  {/* Decorative background elements */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-blue-400/10 to-purple-400/10 rounded-full blur-3xl"></div>
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-green-400/10 to-blue-400/10 rounded-full blur-3xl"></div>

                  <div className="relative z-10">
                    {/* Header */}
                    <div className="text-center mb-8">
                      <p className="text-gray-600 font-bold uppercase max-w-[300px] m-auto">Hãy điền thông tin chính xác để nhận mã OTP kết nối Wi-Fi ngay!</p>

                      <div className="w-20 h-1 bg-[#0065b2] rounded-full mx-auto mt-3"></div>
                    </div>

                    {/* Form Fields */}
                    <div className="space-y-4">
                      {/* Phone Number */}
                      {adSettings?.phone_number === "true" && (
                        <div className="group">
                          <div className="relative space-y-2">
                            <input
                              type="tel"
                              id="phoneNumber"
                              name="phoneNumber"
                              value={surveyData.phoneNumber}
                              maxLength={10}
                              minLength={10}
                              onChange={onInputChange}
                              placeholder={intl.formatMessage({
                                id: "adRenderer.phoneNumberSurvey2",
                              })}
                              className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-300 bg-white/70 backdrop-blur-sm placeholder-gray-400 text-gray-800"
                              required
                            />
                          </div>
                        </div>
                      )}

                      {/* Personal Information Section */}
                      <div className="space-y-4">
                        {/* Full Name */}
                        {adSettings?.fullname === "true" && (
                          <div className="relative space-y-2">
                            <input
                              type="text"
                              id="fullName"
                              name="fullName"
                              value={surveyData.fullName}
                              onChange={onInputChange}
                              placeholder={intl.formatMessage({
                                id: "adRenderer.fullName",
                              })}
                              className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-300 bg-white/70 backdrop-blur-sm placeholder-gray-400 text-gray-800"
                              required
                            />
                          </div>
                        )}

                        {/* Email */}
                        {adSettings?.email === "true" && (
                          <div className="relative space-y-2">
                            <input
                              type="email"
                              id="email"
                              name="email"
                              value={surveyData.email}
                              onChange={onInputChange}
                              placeholder={intl.formatMessage({
                                id: "adRenderer.email",
                              })}
                              className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-300 bg-white/70 backdrop-blur-sm placeholder-gray-400 text-gray-800"
                              required
                            />
                          </div>
                        )}

                        {/* Birth Date */}
                        {adSettings?.BoD === "true" && (
                          <div className="space-y-2">
                            <input
                              type="date"
                              id="birthdate"
                              name="birthdate"
                              value={surveyData.birthdate}
                              onChange={onInputChange}
                              className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-300 bg-white/70 backdrop-blur-sm"
                              required
                            />
                          </div>
                        )}

                        {/* Gender */}
                        {adSettings?.gender === "true" && (
                          <div className="space-y-2">
                            <fieldset>
                              <label className="block text-sm font-semibold text-gray-700 mb-2 ml-1">
                                {intl.formatMessage({
                                  id: "adRenderer.gender",
                                })}{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <div className="grid grid-cols-1 gap-3">
                                <label className="flex items-center p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all duration-200 cursor-pointer group">
                                  <input type="radio" name="gender" value="Nam" checked={surveyData.gender === "Nam"} onChange={onInputChange} className="w-4 h-4 text-blue-600 border-2 border-gray-300 focus:ring-blue-500 focus:ring-2" required />
                                  <span className="ml-3 text-gray-700 group-hover:text-blue-700 transition-colors duration-200">
                                    {intl.formatMessage({
                                      id: "adRenderer.male",
                                    })}
                                  </span>
                                </label>
                                <label className="flex items-center p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all duration-200 cursor-pointer group">
                                  <input type="radio" name="gender" value="Nữ" checked={surveyData.gender === "Nữ"} onChange={onInputChange} className="w-4 h-4 text-blue-600 border-2 border-gray-300 focus:ring-blue-500 focus:ring-2" required />
                                  <span className="ml-3 text-gray-700 group-hover:text-blue-700 transition-colors duration-200">
                                    {intl.formatMessage({
                                      id: "adRenderer.female",
                                    })}
                                  </span>
                                </label>
                                <label className="flex items-center p-2 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all duration-200 cursor-pointer group">
                                  <input
                                    type="radio"
                                    name="gender"
                                    value="Chưa rõ"
                                    checked={surveyData.gender === "Chưa rõ"}
                                    onChange={onInputChange}
                                    className="w-4 h-4 text-blue-600 border-2 border-gray-300 focus:ring-blue-500 focus:ring-2"
                                    required
                                  />
                                  <span className="ml-2 text-gray-700 group-hover:text-blue-700 transition-colors duration-200 text-sm">
                                    {intl.formatMessage({
                                      id: "adRenderer.other",
                                    })}
                                  </span>
                                </label>
                              </div>
                            </fieldset>
                          </div>
                        )}

                        {/* Optional Question */}
                        {adSettings?.is_optional_question === "true" && (
                          <div className="bg-white/50 backdrop-blur-sm rounded-xl p-3 border border-gray-200">
                            <label className="block font-semibold mb-4 text-gray-700">{adSettings?.optional_question}</label>

                            {adSettings?.type_optional_answer === "select-multiple" && (
                              <div className={Array.from(new Set(JSON.parse(adSettings.optional_answer || "[]"))).length <= 4 ? "grid grid-cols-1 gap-3" : "space-y-3"}>
                                {Array.from(new Set(JSON.parse(adSettings.optional_answer || "[]") || [])).map((answer: any, index) => (
                                  <label key={index} className="flex items-center p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all duration-200 cursor-pointer group">
                                    <input
                                      type="checkbox"
                                      name="optionalSurveyAnswer"
                                      required={surveyData.optionalSurveyAnswer.length === 0}
                                      value={typeof answer === "string" || typeof answer === "number" ? answer : String(answer)}
                                      onChange={(e) => {
                                        const { value, checked } = e.target;
                                        setSurveyData((prev: any) => {
                                          const currentAnswers = Array.isArray(prev.optionalSurveyAnswer) ? prev.optionalSurveyAnswer : prev.optionalSurveyAnswer ? [prev.optionalSurveyAnswer] : [];
                                          let updatedAnswers;
                                          if (checked) {
                                            updatedAnswers = [...currentAnswers, value];
                                          } else {
                                            updatedAnswers = currentAnswers.filter((item: any) => item !== value);
                                          }
                                          return {
                                            ...prev,
                                            optionalSurveyAnswer: updatedAnswers,
                                          };
                                        });
                                      }}
                                      className="w-4 h-4 text-blue-600 border-2 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                    />
                                    <span className="ml-3 text-gray-700 group-hover:text-blue-700 transition-colors duration-200">{answer}</span>
                                  </label>
                                ))}
                              </div>
                            )}

                            {adSettings?.type_optional_answer === "select" && (
                              <div className="relative">
                                <select
                                  name="optionalSurveyAnswer"
                                  required
                                  onChange={(e) => {
                                    const { name, value } = e.target;
                                    setSurveyData((prev: any) => ({
                                      ...prev,
                                      [name]: value,
                                    }));
                                    ReactGA.event({
                                      category: "Survey",
                                      action: "Input Changed",
                                      label: `Field: ${name}`,
                                    });
                                  }}
                                  className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-300 bg-white/70 backdrop-blur-sm appearance-none"
                                >
                                  <option value="">
                                    {intl.formatMessage({
                                      id: "adRenderer.selectOption",
                                    })}
                                  </option>
                                  {(JSON.parse(adSettings.optional_answer || "[]") || []).map((answer: any, index: number) => {
                                    if (answer === null || answer === undefined) return null;
                                    let value = typeof answer === "string" || typeof answer === "number" ? answer : typeof answer === "boolean" ? (answer ? "true" : "false") : String(answer);
                                    return (
                                      <option key={index} value={value}>
                                        {value}
                                      </option>
                                    );
                                  })}
                                </select>
                                <div className="absolute right-4 top-4 pointer-events-none">
                                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </div>
                              </div>
                            )}

                            {adSettings?.type_optional_answer === "input" && (
                              <input
                                className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-300 bg-white/70 backdrop-blur-sm"
                                type="text"
                                name="optionalSurveyAnswer"
                                onChange={onInputChange}
                                required
                              />
                            )}
                          </div>
                        )}

                        {adSettings?.is_optional_question_1 === "true" && (
                          <div className="bg-white/50 backdrop-blur-sm rounded-xl p-3 border border-gray-200">
                            <label className="block font-semibold mb-4 text-gray-700">{adSettings?.optional_question_1}</label>

                            {adSettings?.type_optional_answer_1 === "select-multiple" && (
                              <div className="grid grid-cols-2 gap-2">
                                {Array.from(new Set(JSON.parse(adSettings.optional_answer_1 || "[]") || [])).map((answer: any, index) => {
                                  const currentAnswers = Array.isArray(surveyData.optionalSurveyAnswer1) ? surveyData.optionalSurveyAnswer1 : surveyData.optionalSurveyAnswer1 ? [surveyData.optionalSurveyAnswer1] : [];
                                  const isChecked = currentAnswers.includes(String(answer));

                                  return (
                                    <label key={index} className="relative flex items-center cursor-pointer">
                                      <input
                                        type="checkbox"
                                        name="optionalSurveyAnswer1"
                                        required={!currentAnswers || currentAnswers.length === 0}
                                        value={typeof answer === "string" || typeof answer === "number" ? answer : String(answer)}
                                        checked={isChecked}
                                        onChange={(e) => {
                                          const { value, checked } = e.target;
                                          setSurveyData((prev: any) => {
                                            const currentAnswers = Array.isArray(prev.optionalSurveyAnswer1) ? prev.optionalSurveyAnswer1 : prev.optionalSurveyAnswer1 ? [prev.optionalSurveyAnswer1] : [];
                                            let updatedAnswers1;
                                            if (checked) {
                                              updatedAnswers1 = [...currentAnswers, value];
                                            } else {
                                              updatedAnswers1 = currentAnswers.filter((item: any) => item !== value);
                                            }
                                            return {
                                              ...prev,
                                              optionalSurveyAnswer1: updatedAnswers1,
                                            };
                                          });
                                        }}
                                        className="peer sr-only"
                                      />
                                      <div className="w-4 h-4 border-2 border-gray-400 rounded-sm flex items-center justify-center bg-white peer-checked:bg-white peer-checked:border-gray-600 transition-all duration-200 flex-shrink-0">
                                        {isChecked && (
                                          <svg className="w-3 h-3 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                          </svg>
                                        )}
                                      </div>
                                      <span className="ml-2 text-sm text-gray-700 select-none leading-tight">{answer}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            )}

                            {adSettings?.type_optional_answer_1 === "select" && (
                              <div className="relative">
                                <select
                                  name="optionalSurveyAnswer1"
                                  required
                                  onChange={(e) => {
                                    const { name, value } = e.target;
                                    setSurveyData((prev: any) => ({
                                      ...prev,
                                      [name]: value,
                                    }));
                                    ReactGA.event({
                                      category: "Survey",
                                      action: "Input Changed",
                                      label: `Field: ${name}`,
                                    });
                                  }}
                                  className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-300 bg-white/70 backdrop-blur-sm appearance-none"
                                >
                                  <option value="">
                                    {intl.formatMessage({
                                      id: "adRenderer.selectOption",
                                    })}
                                  </option>
                                  {(JSON.parse(adSettings.optional_answer_1 || "[]") || []).map((answer: any, index: number) => {
                                    if (answer === null || answer === undefined) return null;
                                    let value = typeof answer === "string" || typeof answer === "number" ? answer : typeof answer === "boolean" ? (answer ? "true" : "false") : String(answer);
                                    return (
                                      <option key={index} value={value}>
                                        {value}
                                      </option>
                                    );
                                  })}
                                </select>
                                <div className="absolute right-4 top-4 pointer-events-none">
                                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </div>
                              </div>
                            )}

                            {adSettings?.type_optional_answer_1 === "input" && (
                              <input
                                className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-300 bg-white/70 backdrop-blur-sm"
                                type="text"
                                name="optionalSurveyAnswer1"
                                onChange={onInputChange}
                                required
                              />
                            )}
                          </div>
                        )}

                        {adSettings?.is_optional_question_2 === "true" && (
                          <div className="bg-white/50 backdrop-blur-sm rounded-xl p-3 border border-gray-200">
                            <label className="block font-semibold mb-4 text-gray-700">{adSettings?.optional_question_2}</label>

                            {adSettings?.type_optional_answer_2 === "select-multiple" && (
                              <div className="grid grid-cols-2 gap-2">
                                {Array.from(new Set(JSON.parse(adSettings.optional_answer_2 || "[]") || [])).map((answer: any, index) => {
                                  const currentAnswers = Array.isArray(surveyData.optionalSurveyAnswer2) ? surveyData.optionalSurveyAnswer2 : surveyData.optionalSurveyAnswer2 ? [surveyData.optionalSurveyAnswer2] : [];
                                  const isChecked = currentAnswers.includes(String(answer));

                                  return (
                                    <label key={index} className="relative flex items-center cursor-pointer">
                                      <input
                                        type="checkbox"
                                        name="optionalSurveyAnswer2"
                                        required={!currentAnswers || currentAnswers.length === 0}
                                        value={typeof answer === "string" || typeof answer === "number" ? answer : String(answer)}
                                        checked={isChecked}
                                        onChange={(e) => {
                                          const { value, checked } = e.target;
                                          setSurveyData((prev: any) => {
                                            const currentAnswers = Array.isArray(prev.optionalSurveyAnswer2) ? prev.optionalSurveyAnswer2 : prev.optionalSurveyAnswer2 ? [prev.optionalSurveyAnswer2] : [];
                                            let updatedAnswers2;
                                            if (checked) {
                                              updatedAnswers2 = [...currentAnswers, value];
                                            } else {
                                              updatedAnswers2 = currentAnswers.filter((item: any) => item !== value);
                                            }
                                            return {
                                              ...prev,
                                              optionalSurveyAnswer2: updatedAnswers2,
                                            };
                                          });
                                        }}
                                        className="peer sr-only"
                                      />
                                      <div className="w-4 h-4 border-2 border-gray-400 rounded-sm flex items-center justify-center bg-white peer-checked:bg-white peer-checked:border-gray-600 transition-all duration-200 flex-shrink-0">
                                        {isChecked && (
                                          <svg className="w-3 h-3 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                          </svg>
                                        )}
                                      </div>
                                      <span className="ml-2 text-sm text-gray-700 select-none leading-tight">{answer}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            )}

                            {adSettings?.type_optional_answer_2 === "select" && (
                              <div className="relative">
                                <select
                                  name="optionalSurveyAnswer2"
                                  required
                                  onChange={(e) => {
                                    const { name, value } = e.target;
                                    setSurveyData((prev: any) => ({
                                      ...prev,
                                      [name]: value,
                                    }));
                                    ReactGA.event({
                                      category: "Survey",
                                      action: "Input Changed",
                                      label: `Field: ${name}`,
                                    });
                                  }}
                                  className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-300 bg-white/70 backdrop-blur-sm appearance-none"
                                >
                                  <option value="">
                                    {intl.formatMessage({
                                      id: "adRenderer.selectOption",
                                    })}
                                  </option>
                                  {(JSON.parse(adSettings.optional_answer_2 || "[]") || []).map((answer: any, index: number) => {
                                    if (answer === null || answer === undefined) return null;
                                    let value = typeof answer === "string" || typeof answer === "number" ? answer : typeof answer === "boolean" ? (answer ? "true" : "false") : String(answer);
                                    return (
                                      <option key={index} value={value}>
                                        {value}
                                      </option>
                                    );
                                  })}
                                </select>
                                <div className="absolute right-4 top-4 pointer-events-none">
                                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </div>
                              </div>
                            )}

                            {adSettings?.type_optional_answer_2 === "input" && (
                              <input
                                className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-300 bg-white/70 backdrop-blur-sm"
                                type="text"
                                name="optionalSurveyAnswer2"
                                onChange={onInputChange}
                                required
                              />
                            )}
                          </div>
                        )}
                      </div>

                      {/* Terms and Conditions */}
                      <div className="space-y-4">
                        <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                          <label className="flex items-start space-x-3 cursor-pointer group">
                            <input
                              type="checkbox"
                              name="agreeToTerms"
                              checked={isAgree}
                              onChange={() => setIsAgree(!isAgree)}
                              className="mt-1 w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                              required
                              disabled
                            />
                            <span className="flex-1 text-gray-700 leading-relaxed text-start">
                              {intl.formatMessage({
                                id: "consent.dataCollectionAgreement",
                              })}{" "}
                              <span className="text-red-500">*</span>
                            </span>
                          </label>
                        </div>

                        <div className="text-center">
                          <span className="text-gray-600">
                            {intl.formatMessage({ id: "consent.viewDetails" })}{" "}
                            <button
                              type="button"
                              onClick={() => {
                                window.location.href = "https://www.samsung.com/vn/info/privacy/";
                              }}
                              className="text-blue-600 hover:text-blue-700 underline font-medium transition-colors duration-200"
                            >
                              {intl.formatMessage({ id: "consent.here" })}
                            </button>
                          </span>
                        </div>
                      </div>

                      {/* Submit Button */}
                      <button
                        type="submit"
                        className="relative w-full py-3 rounded-xl font-bold uppercase text-lg tracking-wide text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:scale-[1.02] hover:shadow-xl group overflow-hidden"
                        style={{
                          background: `linear-gradient(135deg, ${adSettings?.button_color || "#0065b2"}, ${adSettings?.button_color || "#0065b2"}dd)`,
                        }}
                        disabled={!isAgree || contentCountdown > 0 || isSubmitting}
                      >
                        {/* Button shine effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>

                        <span className="relative z-10">{intl.formatMessage({ id: "adRenderer.confirm" })}</span>
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Survey2;
